import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { Form, Field, Formik } from "formik";
import Select from "../FormikFields/Select";
import { trainingMinutesOptions } from "../../constants/index";
import { TextField } from "formik-mui";
import * as Yup from "yup";

const descriptionMaxCharacters = 800;

const getPolicyVersionDetailsSchema = t =>
  Yup.object().shape({
    name: Yup.string()
      .max(
        150,
        t("validationMessages.cannotExceedLength", {
          type: t("courseVersion.details.title"),
          maxLength: 150
        })
      )
      .required(t("validationMessages.fieldRequired")),
    number: Yup.string().required(t("validationMessages.policyNumberRequired")),
    description: Yup.string().max(
      800,
      t("validationMessages.cannotExceedLength", {
        type: t("courseVersion.details.description"),
        maxLength: 800
      })
    ),
    trainingHours: Yup.number()
      .typeError(t("validationMessages.validNumber"))
      .min(0, t("validationMessages.validNumber"))
      .required(t("validationMessages.fieldRequired")),
    overviewText: Yup.mixed().when("showOverview", {
      is: true,
      then: Yup.mixed()
        .required(t("validationMessages.fieldRequired"))
        .test(
          "validOverviewTextLength",
          t("validationMessages.cannotExceedLength", {
            type: "Course Instructions & Learning Objectives",
            maxLength: 800
          }),
          function() {
            return (
              !this.parent.overviewTextOnlyLength ||
              this.parent.overviewTextOnlyLength <= 800
            );
          }
        )
    })
  });

const PolicyForm = ({
  render,
  initialValues,
  onSubmit,
  disabled,
  addNewVersion,
  versionsMenuItems,
  copyFromExisting,
  activeVersion,
  handleChangeVersion
}) => {
  const { t } = useTranslation();

  const languageMenuItems = [
    { value: "English", label: t("courseVersion.details.languageEnOption") },
    { value: "Spanish", label: t("courseVersion.details.languageEsOption") }
  ];

  const statusMenuItems = [
    { value: "Active", label: t("courseVersion.details.statusActiveOption") },
    {
      value: "Inactive",
      label: t("courseVersion.details.statusInactiveOption")
    }
  ];

  const trainingMinutesMenuItems = trainingMinutesOptions.map(option => ({
    value: option,
    label: option
  }));

  const disabledFields = disabled || (!activeVersion && copyFromExisting);

  return (
    <Formik
      validationSchema={getPolicyVersionDetailsSchema(t)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        submitForm,
        isSubmitting,
        values,
        resetForm,
        setFieldValue,
        setSubmitting
      }) => (
        <Form>
          {render({
            formFields: {
              version: (
                <Select
                  disabled={disabled}
                  name="version"
                  label={t("courseVersion.details.chooseVersionMessage")}
                  menuItems={versionsMenuItems}
                  onChange={version => handleChangeVersion(version)}
                />
              ),
              number: (
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseVersion.details.number")}
                  component={TextField}
                  name="number"
                  disabled={isSubmitting || disabledFields}
                  inputProps={{
                    "data-qa": "policy-form-number-field",
                    onBlur: () => (addNewVersion ? "" : submitForm())
                  }}
                />
              ),
              name: (
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseVersion.details.title")}
                  component={TextField}
                  name="name"
                  disabled={isSubmitting || disabledFields}
                  inputProps={{
                    "data-qa": "policy-form-title-field",
                    onBlur: () => (addNewVersion ? "" : submitForm())
                  }}
                />
              ),
              description: (
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseVersion.details.description")}
                  component={TextField}
                  name="description"
                  multiline
                  helperText={t("courseVersion.details.descriptionHelperText", {
                    characters:
                      descriptionMaxCharacters - values.description.length,
                    maxCharacters: descriptionMaxCharacters
                  })}
                  rows={10}
                  disabled={isSubmitting || disabledFields}
                  inputProps={{
                    "data-qa": "policy-form-description-field",
                    onBlur: () => (addNewVersion ? "" : submitForm())
                  }}
                />
              ),
              language: (
                <Select
                  disabled={disabledFields}
                  name="language"
                  label={t("courseVersion.details.language")}
                  menuItems={languageMenuItems}
                  submitOnChange={addNewVersion ? false : true}
                />
              ),
              status: (
                <Select
                  disabled={disabledFields}
                  name="status"
                  label={t("courseVersion.details.status")}
                  menuItems={statusMenuItems}
                  submitOnChange={addNewVersion ? false : true}
                />
              ),
              trainingHours: (
                <Field
                  disabled={isSubmitting || disabledFields}
                  variant="filled"
                  fullWidth
                  label={t("courseVersion.details.trainingHours")}
                  component={TextField}
                  name="trainingHours"
                  type="number"
                  inputProps={{
                    "data-qa": "policy-form-training-hours-field",
                    onBlur: () => (addNewVersion ? "" : submitForm())
                  }}
                />
              ),
              trainingMinutes: (
                <Select
                  disabled={disabledFields}
                  name="trainingMinutes"
                  menuItems={trainingMinutesMenuItems}
                  renderValue={value =>
                    t("courseVersion.details.minuteDuration", {
                      minutes: value
                    })
                  }
                  submitOnChange={!addNewVersion}
                />
              )
            },
            formikProps: {
              resetForm,
              values,
              submitForm,
              isSubmitting,
              setFieldValue,
              setSubmitting
            }
          })}
        </Form>
      )}
    </Formik>
  );
};

PolicyForm.propTypes = {
  render: PropTypes.func,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    number: PropTypes.string,
    description: PropTypes.string,
    language: PropTypes.string,
    status: PropTypes.string,
    trainingHours: PropTypes.number,
    trainingMinutes: PropTypes.number
  }),
  onSubmit: PropTypes.func,
  disabled: PropTypes.bool
};

export default PolicyForm;
